import { useMemo, type ReactElement } from 'react'
import { isFakeCompany, isTantalim, type ConversationFilter } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Group, Stack, Text, TextInput } from '@mantine/core'
import { useDebounce } from '@hooks'
import {
  toShowingCountText,
  useInfiniteConversations,
  useProfile,
  usePermissionCheck,
  useTranslation,
  useWorkflowForCanvassFeature,
} from '@front/model'
import { Page, TButton } from '@components'
import { useIsClassifiedFilterAvailable } from './useIsClassifiedFilterAvailable'
import { useConversationColumns } from './useColumns'
import { dateRangeToFilter, useConversationClientState } from './useClientState'
import { ToggleConversationColumns } from './ToggleConversationColumns'
import { ConversationsTable } from './table/ConversationsTable'
import { toConversationSort } from './sort'
import { ConversationFilters } from './filters'
import { CreateConversationDrawer } from './CreateConversationDrawer'

function Conversations(): ReactElement {
  const { t } = useTranslation()
  const { company } = useProfile()
  const manage = usePermissionCheck({ permissionType: 'conversation:manage', targetId: company?.companyId })
  const canGenerate = isTantalim(company) || isFakeCompany(company)
  const clientState = useConversationClientState()
  const { range, filter, query, setQuery, sorting, setSorting } = clientState
  const { columns, selectedColumns, setSelectedColumns } = useConversationColumns()
  const debouncedQuery = useDebounce(query, 500)
  const isClassifiedFilterAvailable = useIsClassifiedFilterAvailable()
  const { isEnabled: isWorkflowEnabled } = useWorkflowForCanvassFeature()

  const apiFilter = useMemo((): ConversationFilter => {
    const dateFilter = dateRangeToFilter(range)
    return {
      ...filter,
      classified: isClassifiedFilterAvailable ? filter.classified : undefined,
      stageIds: isWorkflowEnabled ? filter.stageIds : undefined,
      date: dateFilter,
      query: debouncedQuery || undefined,
    }
  }, [range, filter, debouncedQuery, isClassifiedFilterAvailable, isWorkflowEnabled])

  const { sortBy, sortOrder } = useMemo(() => toConversationSort(sorting), [sorting])

  const serverState = useInfiniteConversations({ filter: apiFilter, sortBy, sortOrder })
  const { data, total, isFetching, refetch } = serverState
  const totalCount = total ?? data.length

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Skill Insights')}>
        {manage.isSuccess && manage.data === true && (
          <TButton component={Link} to='/conversations/flagged' icon='flag' variant='secondary'>
            {t('Flagged conversations')}
          </TButton>
        )}
        {canGenerate && <CreateConversationDrawer />}
        <TButton component={Link} to='/conversations/skillShare' icon='local_library' variant='secondary'>
          {t('Skill Share')}
        </TButton>
      </Page.Heading>

      <Page.FunctionBar>
        <Stack w='100%' gap='sm'>
          <Group justify='space-between' gap='sm'>
            <Group gap='sm'>
              <TextInput placeholder={t('Filter')} value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
              <Text size='sm' c='dimmed'>
                {toShowingCountText(data.length, totalCount, 'conversation', 'conversations')}
              </Text>
            </Group>
            <ToggleConversationColumns
              columns={columns}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
            />
          </Group>
          <ConversationFilters clientState={clientState} />
        </Stack>
      </Page.FunctionBar>

      <Page.Content>
        <ConversationsTable
          selectedColumns={selectedColumns}
          sorting={sorting}
          setSorting={setSorting}
          {...serverState}
        />
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/conversations/')({
  component: Conversations,
})
